import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError, getToken } from "../lib/api";
import { useActiveCategories, useCategoryLabel } from "../lib/meta";
import { formatINR } from "../lib/format";
import type {
  Card,
  ParsedStatementRow,
  StatementImportRecord,
  StatementPreview,
} from "../lib/types";
import {
  Badge,
  EmptyState,
  ErrorBanner,
  PageHeader,
  Panel,
  Spinner,
} from "../components/ui";
import { Link } from "react-router-dom";

interface RowState extends ParsedStatementRow {
  remember: boolean;
}

export default function ImportPage() {
  const categories = useActiveCategories();
  const categoryLabel = useCategoryLabel();
  const [cards, setCards] = useState<Card[]>([]);
  const [cardId, setCardId] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState("");
  const [needsPassword, setNeedsPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [preview, setPreview] = useState<StatementPreview | null>(null);
  const [rows, setRows] = useState<RowState[]>([]);
  const [showSkipped, setShowSkipped] = useState(false);
  const [imports, setImports] = useState<StatementImportRecord[] | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const loadCards = useCallback(async () => {
    try {
      const r = await api.get<{ cards: Card[] }>("/cards");
      setCards(r.cards);
      setCardId((current) => current || r.cards.find((c) => c.isActive)?.id || "");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load your cards");
    }
  }, []);

  const loadImports = useCallback(async () => {
    try {
      const r = await api.get<{ imports: StatementImportRecord[] }>("/statements/imports");
      setImports(r.imports);
    } catch {
      setImports([]);
    }
  }, []);

  useEffect(() => { void loadCards(); void loadImports(); }, [loadCards, loadImports]);

  const card = cards.find((c) => c.id === cardId) ?? null;

  async function parse() {
    if (!file || !cardId) return;
    setBusy(true);
    setError(null);
    setNotice(null);

    const form = new FormData();
    form.set("file", file);
    form.set("cardId", cardId);
    if (password) form.set("password", password);

    try {
      // FormData sets its own multipart boundary, so this goes around the
      // JSON helper rather than through it.
      const res = await fetch("/api/statements/parse", {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken() ?? ""}` },
        body: form,
      });
      const body = await res.json();
      if (!res.ok) {
        setNeedsPassword(Boolean(body.needsPassword));
        setError(body.error ?? "The statement could not be read");
        setPreview(null);
        setRows([]);
        return;
      }
      setNeedsPassword(false);
      setPreview(body as StatementPreview);
      setRows((body as StatementPreview).rows.map((r) => ({ ...r, remember: false })));
    } catch {
      setError("The upload failed. Check the server is running and try again.");
    } finally {
      setBusy(false);
    }
  }

  function update(index: number, patch: Partial<RowState>) {
    setRows((current) => current.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  /**
   * One shop can fill a statement — a month at the same canteen is fifty
   * identical lines. Categorising each by hand is the sort of thing that
   * stops people importing at all, so a correction can be applied to every
   * row with the same merchant at once.
   */
  function updateMatching(merchant: string, patch: Partial<RowState>) {
    const key = merchant.trim().toUpperCase();
    setRows((current) =>
      current.map((r) => (r.merchant.trim().toUpperCase() === key ? { ...r, ...patch } : r)),
    );
  }

  const merchantCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of rows) {
      const key = r.merchant.trim().toUpperCase();
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [rows]);

  const selected = rows.filter((r) => r.include);
  // "Remember next time" for every row being imported at once: ticked when
  // all of them are, a dash when only some are.
  const rememberedCount = selected.filter((r) => r.remember).length;
  const rememberAll = selected.length > 0 && rememberedCount === selected.length;
  const rememberSome = rememberedCount > 0 && !rememberAll;
  const selectedTotal = selected.reduce((sum, r) => sum + r.amount, 0);
  /**
   * Where the ticked spending falls. Computed here rather than server-side so
   * it follows the ticks and the corrections, which is the whole point of
   * looking at it before importing.
   */
  const byCategory = useMemo(() => {
    const totals = new Map<string, { count: number; total: number }>();
    for (const row of selected) {
      const entry = totals.get(row.category) ?? { count: 0, total: 0 };
      entry.count += 1;
      entry.total += row.amount;
      totals.set(row.category, entry);
    }
    return [...totals.entries()]
      .map(([category, v]) => ({ category, ...v }))
      .sort((a, b) => b.total - a.total);
  }, [selected]);
  const biggestCategory = byCategory[0]?.total ?? 0;

  /** What the statement claims, against what is actually ticked. */
  const reconciliation = useMemo(() => {
    if (!preview?.statedTotal) return null;
    const difference = Math.round((selectedTotal - preview.statedTotal) * 100) / 100;
    return { stated: preview.statedTotal, difference };
  }, [preview, selectedTotal]);

  async function commit() {
    if (!preview || selected.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<{
        imported: number;
        confirmed: number;
        skipped: number;
        errors: { row: number; message: string }[];
      }>(
        "/statements/commit",
        {
          cardId: preview.cardId,
          fileName: preview.fileName,
          fileHash: preview.fileHash,
          parserKey: preview.parserKey,
          periodStart: preview.periodStart,
          periodEnd: preview.periodEnd,
          statedTotal: preview.statedTotal,
          statedPoints: preview.statedPoints,
          rowsFound: preview.summary.found,
          rows: selected.map((r) => ({
            date: r.date,
            merchant: r.merchant,
            description: r.description,
            amount: r.amount,
            category: r.category,
            channel: r.channel,
            isInternational: r.isInternational,
            sourceHash: r.sourceHash,
            statementCategory: r.statementCategory,
            cardholderId: r.cardholderId,
            confirmsTransactionId: r.confirmsTransactionId,
            rememberCategory: r.remember,
          })),
        },
      );
      const parts = [`${result.imported} transaction${result.imported === 1 ? "" : "s"} added`];
      if (result.confirmed) parts.push(`${result.confirmed} pending purchase${result.confirmed === 1 ? "" : "s"} confirmed`);
      if (result.skipped) parts.push(`${result.skipped} already there`);
      if (result.errors.length) parts.push(`${result.errors.length} could not be added`);
      setNotice(parts.join(", ") + ".");
      setPreview(null);
      setRows([]);
      setFile(null);
      setPassword("");
      if (fileInput.current) fileInput.current.value = "";
      void loadImports();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "The import failed");
    } finally {
      setBusy(false);
    }
  }

  async function undo(record: StatementImportRecord) {
    if (!confirm(`Remove the ${record.stillPresent} transaction${record.stillPresent === 1 ? "" : "s"} that came from ${record.fileName}?`)) return;
    try {
      const r = await api.del<{ removed: number }>(`/statements/imports/${record.id}`);
      setNotice(`${r.removed} transaction${r.removed === 1 ? "" : "s"} removed.`);
      void loadImports();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not undo that import");
    }
  }

  return (
    <div>
      <PageHeader
        title="Import a statement"
        subtitle="Upload the PDF, CSV or Excel file your issuer sends. Nothing is saved until you have looked at what it read."
      />

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      {notice && (
        <div className="mb-4 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          {notice}
        </div>
      )}

      {cards.filter((c) => c.isActive).length === 0 ? (
        <Panel title="The file" className="mb-6">
          <EmptyState
            title="No cards to import into"
            hint="Add a card first — a statement is read against the card it belongs to, and its earn rules decide what each row is worth."
            action={<Link to="/cards" className="btn-primary">Add a card</Link>}
          />
        </Panel>
      ) : (
      <Panel title="The file" className="mb-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="i-card">Which card is this statement for?</label>
            <select
              id="i-card"
              className="input"
              value={cardId}
              onChange={(e) => setCardId(e.target.value)}
            >
              {cards.filter((c) => c.isActive).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}{c.last4 ? ` ···· ${c.last4}` : ""}
                </option>
              ))}
            </select>
            {cards.length === 0 && (
              <p className="mt-1 text-xs text-ink-500">Add a card first — the earn rules decide what each row is worth.</p>
            )}
          </div>

          <div>
            <label className="label" htmlFor="i-file">Statement file</label>
            <input
              id="i-file"
              ref={fileInput}
              type="file"
              className="input"
              accept=".pdf,.csv,.tsv,.txt,.xlsx,.xlsm"
              onChange={(e) => { setFile(e.target.files?.[0] ?? null); setPreview(null); setRows([]); }}
            />
            <p className="mt-1 text-xs text-ink-500">PDF, CSV or XLSX. A scanned PDF has no text to read.</p>
          </div>

          {(needsPassword || password) && (
            <div>
              <label className="label" htmlFor="i-pass">Statement password</label>
              <input
                id="i-pass"
                type="password"
                className="input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Usually your name and date of birth"
                autoComplete="off"
              />
              <p className="mt-1 text-xs text-ink-500">Used to open the file and never stored.</p>
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button className="btn-primary" disabled={!file || !cardId || busy} onClick={() => void parse()}>
            {busy ? "Reading…" : "Read the statement"}
          </button>
          {file && <span className="text-xs text-ink-500">{file.name}</span>}
        </div>
      </Panel>
      )}

      {busy && !preview && <Spinner label="Reading the statement…" />}

      {preview && (
        <>
          <Panel
            title="What it found"
            subtitle={[
              preview.periodStart && preview.periodEnd
                ? `${preview.periodStart} to ${preview.periodEnd}`
                : null,
              preview.last4 ? `card ending ${preview.last4}` : null,
              `read by the ${preview.parserKey} reader`,
            ].filter(Boolean).join(" · ")}
            className="mb-6"
          >
            {preview.warnings.map((w) => (
              <p key={w} className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">{w}</p>
            ))}

            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
              <Figure label="Lines read" value={String(preview.summary.found)} />
              <Figure label="Spending" value={String(preview.summary.debits)} />
              <Figure label="Credits" value={String(preview.summary.credits)} />
              <Figure label="Already imported" value={String(preview.summary.duplicates)} />
              <Figure label="Need a category" value={String(preview.summary.unclassified)} />
              <Figure label="Ticked" value={`${selected.length} · ${formatINR(selectedTotal)}`} />
            </div>

            {reconciliation && (
              <p className={`mt-3 text-sm ${reconciliation.difference === 0 ? "text-emerald-700" : "text-amber-800"}`}>
                {reconciliation.difference === 0
                  ? `That matches the ${formatINR(reconciliation.stated)} this statement says it totals.`
                  : `The statement says it totals ${formatINR(reconciliation.stated)} — ${formatINR(Math.abs(reconciliation.difference))} ${reconciliation.difference > 0 ? "more" : "less"} than what is ticked. Credits and duplicates account for some of that.`}
              </p>
            )}

            {(preview.pendingSlips.matched > 0 || preview.pendingSlips.stillUnseen.length > 0) && (
              <div className="mt-4 rounded-lg border border-ink-200 p-3">
                <h3 className="label mb-1">Pending purchases</h3>
                {preview.pendingSlips.matched > 0 && (
                  <p className="text-sm text-ink-700">
                    {preview.pendingSlips.matched} row{preview.pendingSlips.matched === 1 ? "" : "s"} on this
                    statement match{preview.pendingSlips.matched === 1 ? "es" : ""} a purchase already logged from a slip or an email alert.
                    Importing confirms {preview.pendingSlips.matched === 1 ? "it" : "them"} rather than
                    adding the spend twice.
                  </p>
                )}
                {preview.pendingSlips.stillUnseen.length > 0 && (
                  <>
                    <p className="mt-2 text-sm text-amber-800">
                      {preview.pendingSlips.stillUnseen.length} pending purchase
                      {preview.pendingSlips.stillUnseen.length === 1 ? "" : "s"} on this card
                      {preview.pendingSlips.stillUnseen.length === 1 ? " does" : " do"} not appear here.
                      That is normal for a purchase made after the statement closed — worth a look otherwise.
                    </p>
                    <ul className="mt-1 space-y-0.5 text-xs text-ink-500">
                      {preview.pendingSlips.stillUnseen.map((slip) => (
                        <li key={slip.id}>
                          {slip.date} · {slip.merchant} · {formatINR(slip.amount)}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}

            {preview.cardholders.length > 0 && (
              <div className="mt-4 rounded-lg border border-ink-200 p-3">
                <h3 className="label mb-1">Add-on cardholders on this statement</h3>
                <p className="mb-2 text-xs text-ink-500">
                  Their spending is recorded against them. The points it earns belong to the card&apos;s
                  primary holder — that is how an add-on card works.
                </p>
                <ul className="space-y-2">
                  {preview.cardholders.map((holder) => (
                    <li key={holder.name} className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="w-48 shrink-0 truncate" title={holder.name}>{holder.name}</span>
                      <select
                        className="input w-56 py-1 text-sm"
                        aria-label={`Who is ${holder.name}?`}
                        value={rows.find((r) => r.cardholder === holder.name)?.cardholderId ?? ""}
                        onChange={(e) => {
                          const personId = e.target.value || null;
                          setRows((current) =>
                            current.map((r) => (r.cardholder === holder.name ? { ...r, cardholderId: personId } : r)),
                          );
                        }}
                      >
                        <option value="">Not attributed</option>
                        {preview.people.map((person) => (
                          <option key={person.id} value={person.id}>{person.name}</option>
                        ))}
                      </select>
                      <span className="text-xs text-ink-500">
                        {rows.filter((r) => r.cardholder === holder.name).length} rows
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {byCategory.length > 0 && (
              <div className="mt-4">
                <h3 className="label mb-2">Where the ticked spending falls</h3>
                <ul className="space-y-1.5">
                  {byCategory.map((c) => (
                    <li key={c.category} className="flex items-center gap-3 text-sm">
                      <span className="w-40 shrink-0 truncate" title={categoryLabel(c.category)}>
                        {categoryLabel(c.category)}
                      </span>
                      <span className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-ink-100">
                        <span
                          className="block h-full rounded-full bg-accent-500"
                          style={{ width: `${biggestCategory ? Math.max(2, (c.total / biggestCategory) * 100) : 0}%` }}
                        />
                      </span>
                      <span className="w-28 shrink-0 text-right tabular-nums">{formatINR(c.total)}</span>
                      <span className="w-16 shrink-0 text-right text-xs text-ink-500">
                        {c.count} row{c.count === 1 ? "" : "s"}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {preview.skipped.length > 0 && (
              <div className="mt-4">
                <button className="btn-ghost text-xs" onClick={() => setShowSkipped((v) => !v)}>
                  {showSkipped ? "Hide" : "Show"} {preview.skipped.length} line{preview.skipped.length === 1 ? "" : "s"} it did not read
                </button>
                {showSkipped && (
                  <ul className="mt-2 space-y-1 rounded-lg bg-ink-50 p-3 text-xs text-ink-600">
                    {preview.skipped.map((s) => (
                      <li key={`${s.lineNo}-${s.raw}`} className="truncate" title={s.raw}>
                        <span className="text-ink-400">line {s.lineNo}</span> — {s.reason}: {s.raw}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </Panel>

          <Panel
            title="Check before importing"
            subtitle="Untick anything that should not be here, and fix any category it guessed wrongly."
            actions={
              <div className="flex gap-2">
                <button className="btn-secondary" onClick={() => setRows((r) => r.map((x) => ({ ...x, include: x.direction === "DEBIT" && !x.duplicate })))}>
                  Reset ticks
                </button>
                <button className="btn-primary" disabled={selected.length === 0 || busy} onClick={() => void commit()}>
                  {busy ? "Importing…" : `Import ${selected.length} row${selected.length === 1 ? "" : "s"}`}
                </button>
              </div>
            }
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-ink-200 text-left text-xs uppercase tracking-wide text-ink-500">
                    <th className="w-8 py-2"><span className="sr-only">Include</span></th>
                    <th className="py-2 pr-3">Date</th>
                    <th className="py-2 pr-3">Merchant</th>
                    <th className="py-2 pr-3 text-right">Amount</th>
                    <th className="py-2 pr-3">Category</th>
                    <th className="py-2 pr-3">Channel</th>
                    <th className="py-2">
                      <label className="flex items-center gap-1.5 normal-case tracking-normal" title="Remember the category of every row being imported, for next time">
                        <input
                          type="checkbox"
                          checked={rememberAll}
                          ref={(el) => {
                            if (el) el.indeterminate = rememberSome;
                          }}
                          disabled={selected.length === 0}
                          onChange={() =>
                            setRows((all) => all.map((r) => (r.include ? { ...r, remember: !rememberAll } : r)))
                          }
                          aria-label="Remember all for next time"
                        />
                        <span className="uppercase tracking-wide">Remember</span>
                        <span className="text-ink-400">all</span>
                      </label>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={row.sourceHash + row.lineNo} className={`border-b border-ink-100 ${row.include ? "" : "opacity-55"}`}>
                      <td className="py-2 align-top">
                        <input
                          type="checkbox"
                          checked={row.include}
                          aria-label={`Import ${row.merchant}`}
                          onChange={(e) => update(index, { include: e.target.checked })}
                        />
                      </td>
                      <td className="py-2 pr-3 align-top whitespace-nowrap tabular-nums">{row.date}</td>
                      <td className="min-w-0 py-2 pr-3 align-top">
                        <input
                          className="input py-1 text-sm"
                          value={row.merchant}
                          aria-label={`Merchant on line ${row.lineNo}`}
                          onChange={(e) => update(index, { merchant: e.target.value })}
                        />
                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                          <span className="truncate text-xs text-ink-400" title={row.raw}>{row.description}</span>
                          {row.cardholder && (
                            <Badge tone={row.cardholderId ? "info" : "neutral"}>
                              spent by {row.cardholder}
                            </Badge>
                          )}
                          {row.confirmsTransactionId && (
                            <Badge tone="good">confirms a pending purchase</Badge>
                          )}
                          {row.duplicate && <Badge tone="warning">already imported</Badge>}
                          {row.direction === "CREDIT" && <Badge tone="info">credit</Badge>}
                          {row.isInternational && <Badge tone="info">{row.foreignCurrency ?? "international"}</Badge>}
                          {row.statementCategory && (
                            <Badge tone={row.matchedOn === "STATEMENT_CATEGORY" ? "good" : "neutral"}>
                              statement says {row.statementCategory}
                            </Badge>
                          )}
                          {!row.categoryMatched && <Badge tone="neutral">guessed</Badge>}
                        </div>
                      </td>
                      <td className="py-2 pr-3 align-top text-right tabular-nums whitespace-nowrap">
                        {formatINR(row.amount)}
                      </td>
                      <td className="py-2 pr-3 align-top">
                        <select
                          className="input py-1 text-sm"
                          value={row.category}
                          aria-label={`Category on line ${row.lineNo}`}
                          onChange={(e) => update(index, { category: e.target.value, remember: true })}
                        >
                          {categories.map((c) => (
                            <option key={c.key} value={c.key}>{c.label}</option>
                          ))}
                        </select>
                        {(merchantCounts.get(row.merchant.trim().toUpperCase()) ?? 0) > 1 && (
                          <button
                            className="btn-ghost mt-1 px-0 text-xs"
                            onClick={() =>
                              updateMatching(row.merchant, {
                                category: row.category,
                                channel: row.channel,
                                remember: true,
                              })
                            }
                          >
                            Apply to all {merchantCounts.get(row.merchant.trim().toUpperCase())}
                          </button>
                        )}
                      </td>
                      <td className="py-2 pr-3 align-top">
                        <select
                          className="input py-1 text-sm"
                          value={row.channel}
                          aria-label={`Channel on line ${row.lineNo}`}
                          onChange={(e) => update(index, { channel: e.target.value as "ONLINE" | "OFFLINE" })}
                        >
                          <option value="OFFLINE">In store</option>
                          <option value="ONLINE">Online</option>
                        </select>
                      </td>
                      <td className="py-2 align-top">
                        <label className="flex items-center gap-1.5 text-xs text-ink-600">
                          <input
                            type="checkbox"
                            checked={row.remember}
                            onChange={(e) => update(index, { remember: e.target.checked })}
                          />
                          next time
                        </label>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </>
      )}

      <Panel title="Past imports" subtitle="Undo removes only the rows that import created." className="mt-6">
        {imports === null ? (
          <Spinner />
        ) : imports.length === 0 ? (
          <EmptyState title="Nothing imported yet" hint="Upload a statement above and it will be listed here." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-200 text-left text-xs uppercase tracking-wide text-ink-500">
                  <th className="py-2 pr-3">File</th>
                  <th className="py-2 pr-3">Card</th>
                  <th className="py-2 pr-3">Period</th>
                  <th className="py-2 pr-3 text-right">Added</th>
                  <th className="py-2 pr-3 text-right">Still here</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {imports.map((i) => (
                  <tr key={i.id} className="border-b border-ink-100">
                    <td className="min-w-0 py-2 pr-3">
                      <span className="block truncate" title={i.fileName}>{i.fileName}</span>
                      <span className="text-xs text-ink-400">{new Date(i.createdAt).toLocaleDateString("en-IN")}</span>
                    </td>
                    <td className="py-2 pr-3">{i.cardName}</td>
                    <td className="py-2 pr-3 whitespace-nowrap text-xs text-ink-500">
                      {i.periodStart && i.periodEnd
                        ? `${i.periodStart.slice(0, 10)} → ${i.periodEnd.slice(0, 10)}`
                        : "—"}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">{i.rowsImported}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{i.stillPresent}</td>
                    <td className="py-2 text-right">
                      <button className="btn-ghost text-xs text-rose-700" onClick={() => void undo(i)}>
                        Undo
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {card && !preview && (
        <p className="mt-6 text-xs text-ink-500">
          Points are worked out by {card.name}&apos;s own earn rules as each row is added, not taken from the
          statement — so they stay right if you correct a rule later.
        </p>
      )}
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-ink-500">{label}</div>
      <div className="font-semibold tabular-nums text-ink-900">{value}</div>
    </div>
  );
}
